import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { StaffNotification } from '@/types';
import { notificationApi } from '@/services/api';
import { logger } from '@/utils/logger';

interface NotificationState {
  notifications: StaffNotification[];
  unreadCount: number;
  loading: boolean;
  pollingTimer: ReturnType<typeof setInterval> | null;
}

interface NotificationActions {
  fetchUnreadCount: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
}

type NotificationStore = NotificationState & NotificationActions;

const POLLING_INTERVAL = 30000; // 30s

export const useNotificationStore = create<NotificationStore>()(
  devtools(
    (set, get) => ({
      // State
      notifications: [],
      unreadCount: 0,
      loading: false,
      pollingTimer: null,

      // Actions
      fetchUnreadCount: async () => {
        try {
          const response = await notificationApi.getUnreadCount();
          if (response.data) {
            set({ unreadCount: response.data.unread_count });
          }
        } catch (error) {
          logger.error('获取未读通知数量失败:', error);
        }
      },

      fetchNotifications: async () => {
        try {
          set({ loading: true });
          const response = await notificationApi.getAll({ limit: 30 });
          if (response.data) {
            set({
              notifications: response.data.notifications,
              loading: false,
            });
          } else {
            set({ loading: false });
          }
        } catch (error) {
          logger.error('获取通知列表失败:', error);
          set({ loading: false });
        }
      },

      markAsRead: async (id: number) => {
        try {
          const response = await notificationApi.markAsRead(id);
          if (!response.error) {
            set((state) => ({
              notifications: state.notifications.map((n) =>
                n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
              ),
              unreadCount: Math.max(0, state.unreadCount - 1),
            }));
          }
        } catch (error) {
          logger.error('标记通知已读失败:', error);
        }
      },

      markAllAsRead: async () => {
        try {
          const response = await notificationApi.markAllAsRead();
          if (!response.error) {
            set((state) => ({
              notifications: state.notifications.map((n) => ({
                ...n,
                is_read: true,
                read_at: n.read_at || new Date().toISOString(),
              })),
              unreadCount: 0,
            }));
          }
        } catch (error) {
          logger.error('批量标记已读失败:', error);
        }
      },

      startPolling: () => {
        const { pollingTimer } = get();
        if (pollingTimer) return; // 已在轮询

        // 立即获取一次
        get().fetchUnreadCount();

        const timer = setInterval(() => {
          // 页面不可见时暂停
          if (document.visibilityState === 'hidden') return;
          get().fetchUnreadCount();
        }, POLLING_INTERVAL);

        set({ pollingTimer: timer });
      },

      stopPolling: () => {
        const { pollingTimer } = get();
        if (pollingTimer) {
          clearInterval(pollingTimer);
          set({ pollingTimer: null });
        }
      },
    }),
    { name: 'NotificationStore' }
  )
);

// 选择器
export const useNotifications = () => {
  const { notifications, unreadCount, loading } = useNotificationStore();
  return { notifications, unreadCount, loading };
};

export const useNotificationActions = () => {
  const { fetchUnreadCount, fetchNotifications, markAsRead, markAllAsRead, startPolling, stopPolling } =
    useNotificationStore();
  return { fetchUnreadCount, fetchNotifications, markAsRead, markAllAsRead, startPolling, stopPolling };
};
